import React, { useState, useEffect } from 'react';
import { db } from '../config/firebase';
import { collection, query, orderBy, limit, onSnapshot, doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import './GameRanking.css';

const GameRanking = ({ gameName, currentScore, user }) => {
  const [topPlayers, setTopPlayers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [userPresenceRef, setUserPresenceRef] = useState(null);

  // Atualizar presença do usuário
  useEffect(() => {
    if (!user) return;

    const presenceRef = doc(db, 'presence', `${gameName}_${user.uid}`);
    
    const updatePresence = async () => {
      try {
        await setDoc(presenceRef, {
          userId: user.uid,
          userName: user.displayName || user.email,
          userPhoto: user.photoURL || null,
          game: gameName,
          online: true,
          lastSeen: serverTimestamp()
        }, { merge: true });
      } catch (error) {
        console.error('Erro ao atualizar presença:', error);
      }
    };

    // Atualizar presença a cada 30 segundos
    updatePresence();
    const presenceInterval = setInterval(updatePresence, 30000);

    // Marcar como offline ao sair
    const handleBeforeUnload = async () => {
      try {
        await updateDoc(presenceRef, {
          online: false,
          lastSeen: serverTimestamp()
        });
      } catch (error) {
        console.error('Erro ao marcar offline:', error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    setUserPresenceRef(presenceRef);

    return () => {
      clearInterval(presenceInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      handleBeforeUnload();
    };
  }, [user, gameName]);

  // Ouvir usuários online em tempo real
  useEffect(() => {
    const presenceQuery = query(collection(db, 'presence'));
    
    const unsubscribe = onSnapshot(presenceQuery, (snapshot) => {
      const now = Date.now();
      const fiveMinutesAgo = now - 5 * 60 * 1000;
      
      const online = snapshot.docs.filter(doc => {
        const data = doc.data();
        const lastSeen = data.lastSeen?.toMillis() || 0;
        return data.game === gameName && 
               data.online === true && 
               lastSeen > fiveMinutesAgo;
      }).length;
      
      setOnlineUsers(online);
    });

    return () => unsubscribe();
  }, [gameName]);

  // Ouvir ranking em tempo real
  useEffect(() => {
    const scoresQuery = query(
      collection(db, 'scores', gameName, 'allScores'),
      orderBy('score', 'desc'),
      limit(10)
    );

    const unsubscribe = onSnapshot(scoresQuery, (snapshot) => {
      const players = snapshot.docs.map((doc, index) => ({
        id: doc.id,
        rank: index + 1,
        ...doc.data()
      }));
      setTopPlayers(players);
    });

    return () => unsubscribe();
  }, [gameName]);

  // Salvar pontuação quando o jogo termina
  useEffect(() => {
    if (currentScore > 0 && user) {
      saveScore(currentScore);
    }
  }, [currentScore, user]);

  const saveScore = async (score) => {
    if (!user || score <= 0) return;

    try {
      const scoreRef = doc(db, 'scores', gameName, 'allScores', user.uid);
      
      await setDoc(scoreRef, {
        userId: user.uid,
        userName: user.displayName || user.email,
        userPhoto: user.photoURL || null,
        score: score,
        timestamp: serverTimestamp(),
        game: gameName
      }, { merge: true });
    } catch (error) {
      console.error('Erro ao salvar pontuação:', error);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Agora';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // segundos

    if (diff < 60) return 'Agora mesmo';
    if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
    return `${Math.floor(diff / 86400)}d atrás`;
  };

  return (
    <div className="game-ranking">
      <div className="ranking-header">
        <div className="online-indicator">
          <span className="online-dot"></span>
          <span className="online-count">{onlineUsers} online</span>
        </div>
        <h3>🏆 Top 10</h3>
      </div>

      <div className="ranking-list">
        {topPlayers.length === 0 ? (
          <div className="ranking-empty">
            <p>Seja o primeiro a pontuar!</p>
          </div>
        ) : (
          topPlayers.map((player) => (
            <div 
              key={player.id} 
              className={`ranking-item ${player.userId === user?.uid ? 'current-user' : ''} rank-${player.rank}`}
            >
              <div className="rank-badge">
                {player.rank === 1 && '🥇'}
                {player.rank === 2 && '🥈'}
                {player.rank === 3 && '🥉'}
                {player.rank > 3 && `#${player.rank}`}
              </div>

              <div className="player-info">
                {player.userPhoto ? (
                  <img src={player.userPhoto} alt={player.userName} className="player-avatar" />
                ) : (
                  <div className="player-avatar-placeholder">
                    {(player.userName || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="player-details">
                  <span className="player-name">
                    {player.userName || 'Anônimo'}
                    {player.userId === user?.uid && ' (Você)'}
                  </span>
                  <span className="player-time">{formatTime(player.timestamp)}</span>
                </div>
              </div>

              <div className="player-score">
                {player.score.toLocaleString()}
              </div>
            </div>
          ))
        )}
      </div>

      {user && currentScore > 0 && (
        <div className="current-score-badge">
          <span>Sua pontuação:</span>
          <strong>{currentScore.toLocaleString()}</strong>
        </div>
      )}
    </div>
  );
};

export default GameRanking;
